/**
 * Copyright (c) VVDN Technologies Pvt Ltd. All rights reserved.
 * Proprietary and confidential — custom algorithms.
 */

/** Canvas layout — maps floor meters (widthM × heightM) to pixels with correct aspect ratio. */

import type { FloorViewport } from './floorViewport'
import { DEFAULT_FLOOR_VIEWPORT, clientToWorldCanvas } from './floorViewport'

export const FLOOR_PAD = 20

export type FloorDrawLayout = {
  pad: number
  /** Full padded box (may contain letterboxing). */
  boxW: number
  boxH: number
  /** Actual floor plan draw area in pixels. */
  x: number
  y: number
  w: number
  h: number
}

export function computeFloorDrawLayout(
  lw: number,
  lh: number,
  widthM: number,
  heightM: number,
  pad = FLOOR_PAD,
): FloorDrawLayout {
  const boxW = Math.max(1, lw - pad * 2)
  const boxH = Math.max(1, lh - pad * 2)
  const floorAspect = widthM / Math.max(1, heightM)
  const boxAspect = boxW / boxH
  let w = boxW
  let h = boxH
  if (boxAspect > floorAspect) {
    h = boxH
    w = boxH * floorAspect
  } else {
    w = boxW
    h = boxW / floorAspect
  }
  return {
    pad,
    boxW,
    boxH,
    x: pad + (boxW - w) / 2,
    y: pad + (boxH - h) / 2,
    w,
    h,
  }
}

export function pctToCanvas(
  xPct: number,
  yPct: number,
  lw: number,
  lh: number,
  widthM: number,
  heightM: number,
  pad = FLOOR_PAD,
) {
  const layout = computeFloorDrawLayout(lw, lh, widthM, heightM, pad)
  return {
    x: layout.x + (xPct / 100) * layout.w,
    y: layout.y + (yPct / 100) * layout.h,
    fw: layout.w,
    fh: layout.h,
    layout,
  }
}

export function clientToPct(
  clientX: number,
  clientY: number,
  canvasRect: DOMRect,
  widthM: number,
  heightM: number,
  pad = FLOOR_PAD,
  clampOutside = false,
  viewport: FloorViewport = DEFAULT_FLOOR_VIEWPORT,
): { x: number; y: number } | null {
  const layout = computeFloorDrawLayout(canvasRect.width, canvasRect.height, widthM, heightM, pad)
  const world = clientToWorldCanvas(clientX, clientY, canvasRect, viewport)
  let lx = world.x - layout.x
  let ly = world.y - layout.y
  if (lx < 0 || ly < 0 || lx > layout.w || ly > layout.h) {
    if (!clampOutside) return null
    lx = Math.min(layout.w, Math.max(0, lx))
    ly = Math.min(layout.h, Math.max(0, ly))
  }
  return { x: (lx / layout.w) * 100, y: (ly / layout.h) * 100 }
}

/** Layout for hit-testing AP markers (screen coordinates). */
export function hitTestLayoutFromCanvas(
  canvasRect: DOMRect,
  widthM: number,
  heightM: number,
  pad = FLOOR_PAD,
  viewport: FloorViewport = DEFAULT_FLOOR_VIEWPORT,
) {
  const layout = computeFloorDrawLayout(canvasRect.width, canvasRect.height, widthM, heightM, pad)
  return {
    left: canvasRect.left,
    top: canvasRect.top,
    ox: layout.x,
    oy: layout.y,
    fw: layout.w,
    fh: layout.h,
    viewport,
  }
}
